/**
 * How anything outside the search button asks the ⌘K search to open: the
 * small-screen header's search button, a page shortcut, a menu handoff.
 *
 * `useNavSearch` (in the side nav) owns the open state and shows the pane
 * beside the rail or the small-screen dialog. A window event keeps the
 * callers decoupled: a caller needs no ref and no context, and the hook
 * remains the only thing that knows how it opens.
 */

import { NAV_MENU_LABEL_SECTIONS } from "@/lib/navigation/nav-manifest";

export const NAV_SEARCH_OPEN_EVENT = "salesably:nav-search-open";

/**
 * A header menu's label and the name the search index files its pages under are
 * not always the same word: the header says "My Results" where the index says
 * "Results", and "Team Reports" where it says "Reports". Both come from the one
 * manifest entry, so a rename cannot leave them disagreeing.
 *
 * An unknown label falls through to itself, matches no section, and the
 * tiebreak simply stops firing — the failure mode is "no preference", never a
 * wrong preference.
 */
export function resolveNavSection(menuLabel) {
  if (!menuLabel) return "";
  return NAV_MENU_LABEL_SECTIONS[menuLabel] || menuLabel;
}

/**
 * Open the global search.
 *
 * @param {object} [options]
 * @param {string} [options.query] Text to seed the input with — typically the
 *   keystroke that triggered the handoff, so no character is lost.
 * @param {boolean} [options.pagesFirst] Rank page links above content results.
 *   Set when the surface the user was looking at was itself a list of pages.
 * @param {string} [options.menuLabel] The nav menu this came from. Used only to
 *   break ties between pages that score identically.
 * @param {{href: string, title: string, subtitle?: string, shortcut?: string}} [options.page]
 *   A page to lead the results, highlighted, so Enter opens it. Set by a page
 *   shortcut; it stays on top until the query is edited.
 */
export function openNavSearch({
  query = "",
  pagesFirst = false,
  menuLabel = "",
  page = null,
} = {}) {
  if (typeof window === "undefined") return;

  window.dispatchEvent(
    new CustomEvent(NAV_SEARCH_OPEN_EVENT, {
      detail: {
        query,
        pagesFirst,
        section: resolveNavSection(menuLabel),
        page,
      },
    }),
  );
}
